import type { RowData, RowSelectionState } from "@tanstack/react-table";
import type { DataGridRow } from "../columns/types";
import type { GridController, SelectionRun } from "./gridController";

const getSelectedIds = (selection: RowSelectionState) =>
    Object.keys(selection).filter((rowId) => selection[rowId]);

/** The rows keep their order, and the selection matches what the run left behind. */
const isRunIntact = ({
    run,
    controller,
    selection,
}: {
    run: SelectionRun;
    controller: GridController;
    selection: RowSelectionState;
}) => {
    const untouchedIds = getSelectedIds(selection).filter(
        (rowId) => !run.touchedRowIds.has(rowId),
    );
    return (
        run.rowIndexById === controller.rowIndexById &&
        run.rowIds.every(
            (rowId) => selection[rowId] && controller.rowIndexById.has(rowId),
        ) &&
        untouchedIds.length === run.untouchedSelectedIds.size &&
        untouchedIds.every((rowId) => run.untouchedSelectedIds.has(rowId))
    );
};

export const endSelectionRun = (controller: GridController) => {
    controller.selectionRun = null;
};

/** Focus moved to `rowId` (undefined for the header or the grid itself). */
export const endSelectionRunOffRow = (
    controller: GridController,
    rowId: string | undefined,
) => {
    if (controller.selectionRun?.focusRowId !== rowId) {
        endSelectionRun(controller);
    }
};

/**
 * One row mode Shift+Up/Down step from `row` to `next`: selects `next`, and
 * deselects `row` when the step moves back toward the run's anchor (the row
 * the run started on). Rows outside the run keep their selection.
 *
 * The run ends, so the next step anchors on the focused row, when any other
 * grid key is pressed (`endSelectionRun`), when focus lands in the grid
 * anywhere but the run's row (`endSelectionRunOffRow`), or when the selection
 * changed some other way: a row the run selected is no longer selected or
 * shown, or a row it never touched changed (toolbar clear, select all, a
 * checkbox, a refetch), or the rows changed order (a sort, a reorder).
 */
export const stepSelectionRun = <TData extends RowData>({
    controller,
    row,
    next,
}: {
    controller: GridController;
    row: DataGridRow<TData>;
    next: DataGridRow<TData>;
}) => {
    const selection = row.table.atoms.rowSelection.get();
    const current = controller.selectionRun;
    const run =
        current?.focusRowId === row.id &&
        isRunIntact({ run: current, controller, selection })
            ? current
            : null;
    const anchorRowId = run?.anchorRowId ?? row.id;
    const getIndex = (rowId: string) =>
        controller.rowIndexById.get(rowId) ?? -1;
    const anchorIndex = getIndex(anchorRowId);
    const isTowardAnchor =
        Math.abs(getIndex(next.id) - anchorIndex) <
        Math.abs(getIndex(row.id) - anchorIndex);

    const touchedRowIds = new Set(run?.touchedRowIds);
    for (const touched of [
        row,
        next,
        ...row.getLeafRows(),
        ...next.getLeafRows(),
    ]) {
        touchedRowIds.add(touched.id);
    }
    const rowIds = new Set(run?.rowIds ?? [row.id]);
    if (isTowardAnchor) {
        rowIds.delete(row.id);
    }
    rowIds.add(next.id);

    row.toggleSelected(!isTowardAnchor);
    next.toggleSelected(true);
    controller.selectionRun = {
        anchorRowId,
        focusRowId: next.id,
        rowIds: [...rowIds],
        touchedRowIds,
        untouchedSelectedIds: new Set(
            getSelectedIds(selection).filter(
                (rowId) => !touchedRowIds.has(rowId),
            ),
        ),
        rowIndexById: controller.rowIndexById,
    };
};
